export class ApiError extends Error {
  status: number;
  detail: string;
  type: string;

  constructor(status: number, detail: string, type: string) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.type = type;
  }
}

interface ApiClientConfig {
  getToken: () => string | null;
  onUnauthorized: () => void;
  baseUrl?: string | undefined;
}

interface AuthCodeRequestResponse {
  object: 'auth_code_request';
  email: string;
  expires_in: number;
}

interface UserResponse {
  object: 'user';
  id: string;
  email: string;
  tenant_id: string;
  role: string;
}

interface AuthTokenResponse {
  object: 'auth_token';
  token: string;
  user: UserResponse;
}

export interface ConversationResponse {
  object: 'conversation';
  id: string;
  title: string;
  last_message_preview: string | null;
  status: string;
  processing_status: 'idle' | 'processing';
  unread: boolean;
  user_id: string;
  tenant_id: string;
  created_at: string;
  updated_at: string;
}

export interface AgentPanelResponse {
  object: 'agent_panel';
  id: string;
  conversation_id: string;
  tab_id: string;
  title: string;
  components: Array<{ type: string; id: string; props: Record<string, unknown> }>;
  created_at: string;
  updated_at: string;
}

interface ListResponse<T> {
  object: 'list';
  data: T[];
  has_more: boolean;
  url: string;
}

export interface MessageResponse {
  object: 'message';
  id: string;
  role: 'human' | 'ai';
  content: string;
  sdk_events?: Record<string, unknown>[];
  created_at?: string | undefined;
}

export interface PlanResponse {
  object: 'plan';
  id: string;
  task_id: string | null;
  conversation_id: string | null;
  tenant_id: string;
  version: number;
  status: 'draft' | 'pending_approval' | 'approved' | 'executing' | 'completed' | 'revised' | 'denied';
  steps: unknown[];
  data_access: unknown[];
  approved_by: string | null;
  denial_reason: string | null;
  metadata: Record<string, unknown>;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

interface TaskResponse {
  object: 'task';
  id: string;
  title: string;
  description: string | null;
  stage: string;
  overlay_state: string | null;
  conversation_id: string;
  assigned_to: string | null;
  metadata: Record<string, unknown>;
  tenant_id: string;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
}

interface FileResponse {
  object: 'file';
  id: string;
  filename: string;
  content_type: string;
  size: number;
  conversation_id: string;
  tenant_id: string;
  created_at: string;
}

export interface CredentialResponse {
  object: 'credential';
  id: string;
  plan_id: string | null;
  tenant_id: string;
  vault_alias: string;
  service_name: string;
  access_level: 'read' | 'read_write';
  access_mode: 'tokenized' | 'cleartext';
  sharing_mode: 'always_cleartext' | 'plan_cleartext' | 'plan_alias';
  isolation: 'tenant' | 'personal' | 'system';
  status: 'pending' | 'active' | 'expired' | 'revoked';
  name: string | null;
  credential_type: string | null;
  created_by: string | null;
  granted_by: string | null;
  revoked_by: string | null;
  revocation_reason: string | null;
  expires_at: string;
  granted_at: string | null;
  revoked_at: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  is_enabled_for_tenant?: boolean;
}

export interface SystemCredentialEnablementResponse {
  object: 'system_credential_enablement';
  id: string;
  credential_id: string;
  credential?: CredentialResponse;
  tenant_id: string;
  enabled_by: string;
  disabled_by: string | null;
  enabled_at: string;
  disabled_at: string | null;
  metadata: Record<string, unknown>;
}

export interface AccessLogEntryResponse {
  object: 'credential_access_log';
  id: string;
  credential_id: string;
  credential_name: string | null;
  credential_service: string | null;
  plan_id: string | null;
  plan_title: string | null;
  action: 'approved' | 'accessed' | 'injected' | 'revoked' | 'enabled' | 'disabled';
  actor_id: string | null;
  actor_name: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface TenantUserResponse {
  object: 'user';
  id: string;
  email: string;
  display_name: string | null;
  role: 'owner' | 'admin' | 'member';
  tenant_id: string;
  deactivated_at: string | null;
  deactivated_by: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

interface CreateCredentialRequest {
  name: string;
  credential_type: string;
  service_name: string;
  access_level: 'read' | 'read_write';
  sharing_mode?: 'always_cleartext' | 'plan_cleartext' | 'plan_alias';
  isolation?: 'tenant' | 'personal' | 'system';
  fields?: Record<string, unknown>;
}

interface UpdateCredentialRequest {
  name?: string;
  sharing_mode?: 'always_cleartext' | 'plan_cleartext' | 'plan_alias';
  isolation?: 'tenant' | 'personal' | 'system';
  fields?: Record<string, unknown>;
}

export interface ChangelogEntryResponse {
  summary: string;
  category: string;
  tiers: string[];
}

export interface ChangelogReleaseResponse {
  object: 'changelog_release';
  id: string;
  date: string;
  stream: string;
  version: string | null;
  packages: Record<string, string> | null;
  entries: ChangelogEntryResponse[];
}

export class ApiClient {
  private config: ApiClientConfig;

  constructor(config: ApiClientConfig) {
    this.config = config;
  }

  private _url(path: string): string {
    return this.config.baseUrl ? `${this.config.baseUrl.replace(/\/$/, '')}${path}` : path;
  }

  private async _fetch(url: string, options: RequestInit = {}): Promise<Response> {
    const token = this.config.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> | undefined),
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(this._url(url), { ...options, headers });

    if (response.status === 401) {
      this.config.onUnauthorized();
    }

    if (!response.ok) {
      let detail = `Request failed with status ${response.status}`;
      let type = 'unknown_error';
      try {
        const body = await response.clone().json() as { detail?: string; type?: string };
        if (body.detail) detail = body.detail;
        if (body.type) type = body.type;
      } catch {
        /* non-JSON error body */
      }
      throw new ApiError(response.status, detail, type);
    }

    return response;
  }

  // Auth endpoints
  async requestCode(email: string): Promise<AuthCodeRequestResponse> {
    const response = await this._fetch('/api/auth/request_code', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    return response.json() as Promise<AuthCodeRequestResponse>;
  }

  async verifyCode(email: string, code: string): Promise<AuthTokenResponse> {
    const response = await this._fetch('/api/auth/verify_code', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
    return response.json() as Promise<AuthTokenResponse>;
  }

  async getMe(): Promise<UserResponse> {
    const response = await this._fetch('/api/auth/me');
    return response.json() as Promise<UserResponse>;
  }

  async tokenExchange(body: Record<string, unknown>): Promise<AuthTokenResponse> {
    const response = await this._fetch('/api/auth/token_exchange', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<AuthTokenResponse>;
  }

  // Conversation endpoints
  async createConversation(body: Record<string, unknown>): Promise<ConversationResponse> {
    const response = await this._fetch('/api/conversations', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<ConversationResponse>;
  }

  async listConversations(query?: Record<string, string>): Promise<ListResponse<ConversationResponse>> {
    const params = query ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(`/api/conversations${params}`);
    return response.json() as Promise<ListResponse<ConversationResponse>>;
  }

  async getConversation(id: string): Promise<ConversationResponse> {
    const response = await this._fetch(`/api/conversations/${id}`);
    return response.json() as Promise<ConversationResponse>;
  }

  async updateConversation(id: string, body: Record<string, unknown>): Promise<ConversationResponse> {
    const response = await this._fetch(`/api/conversations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<ConversationResponse>;
  }

  async listAgentPanels(conversationId: string): Promise<AgentPanelResponse[]> {
    const response = await this._fetch(`/api/conversations/${conversationId}/panels`);
    const list = (await response.json()) as ListResponse<AgentPanelResponse>;
    return list.data;
  }

  async markConversationRead(id: string): Promise<ConversationResponse> {
    const response = await this._fetch(`/api/conversations/${id}/mark_read`, {
      method: 'POST',
    });
    return response.json() as Promise<ConversationResponse>;
  }

  // Streaming messages endpoint — returns raw Response for stream consumption
  async sendMessageStream(
    conversationId: string,
    content: string,
    options?: { signal?: AbortSignal; file_ids?: string[]; stream_events?: string[] },
  ): Promise<Response> {
    const token = this.config.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/x-ndjson',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const body: Record<string, unknown> = { content };
    if (options?.file_ids && options.file_ids.length > 0) {
      body.file_ids = options.file_ids;
    }
    if (options?.stream_events && options.stream_events.length > 0) {
      body.stream_events = options.stream_events;
    }
    const response = await fetch(this._url(`/api/conversations/${conversationId}/messages`), {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      ...(options?.signal ? { signal: options.signal } : {}),
    });
    if (response.status === 401) {
      this.config.onUnauthorized();
    }
    return response;
  }

  // Task endpoints
  async createTask(body: Record<string, unknown>): Promise<TaskResponse> {
    const response = await this._fetch('/api/tasks', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<TaskResponse>;
  }

  async listTasks(query?: Record<string, string>): Promise<ListResponse<TaskResponse>> {
    const params = query ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(`/api/tasks${params}`);
    return response.json() as Promise<ListResponse<TaskResponse>>;
  }

  async getTask(id: string): Promise<TaskResponse> {
    const response = await this._fetch(`/api/tasks/${id}`);
    return response.json() as Promise<TaskResponse>;
  }

  async updateTask(id: string, body: Record<string, unknown>): Promise<TaskResponse> {
    const response = await this._fetch(`/api/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<TaskResponse>;
  }

  async transitionTask(id: string, body: Record<string, unknown>): Promise<TaskResponse> {
    const response = await this._fetch(`/api/tasks/${id}/transitions`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<TaskResponse>;
  }

  async listTaskPlans(taskId: string): Promise<ListResponse<PlanResponse>> {
    const response = await this._fetch(`/api/tasks/${taskId}/plans`);
    return response.json() as Promise<ListResponse<PlanResponse>>;
  }

  async listPlans(query?: Record<string, string>): Promise<ListResponse<PlanResponse>> {
    const params = query ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(`/api/plans${params}`);
    return response.json() as Promise<ListResponse<PlanResponse>>;
  }

  // File endpoints
  async uploadFile(conversationId: string, file: File): Promise<FileResponse> {
    const token = this.config.getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(this._url(`/api/conversations/${conversationId}/files`), {
      method: 'POST',
      headers,
      body: formData,
    });
    if (response.status === 401) {
      this.config.onUnauthorized();
    }
    if (!response.ok) {
      let detail = `Upload failed with status ${response.status}`;
      let type = 'upload_error';
      try {
        const body = await response.clone().json() as { detail?: string; type?: string };
        if (body.detail) detail = body.detail;
        if (body.type) type = body.type;
      } catch { /* non-JSON */ }
      throw new ApiError(response.status, detail, type);
    }
    return response.json() as Promise<FileResponse>;
  }

  async listFiles(conversationId: string): Promise<ListResponse<FileResponse>> {
    const response = await this._fetch(`/api/conversations/${conversationId}/files`);
    return response.json() as Promise<ListResponse<FileResponse>>;
  }

  async getFile(conversationId: string, fileId: string): Promise<FileResponse> {
    const response = await this._fetch(`/api/conversations/${conversationId}/files/${fileId}`);
    return response.json() as Promise<FileResponse>;
  }

  async listMessages(conversationId: string, query?: Record<string, string>): Promise<ListResponse<MessageResponse>> {
    const params = query ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(`/api/conversations/${conversationId}/messages${params}`);
    return response.json() as Promise<ListResponse<MessageResponse>>;
  }

  // Credentials
  async listCredentials(query?: Record<string, string>): Promise<ListResponse<CredentialResponse>> {
    const params = query ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(`/api/credentials${params}`);
    return response.json() as Promise<ListResponse<CredentialResponse>>;
  }

  async getCredential(id: string): Promise<CredentialResponse> {
    const response = await this._fetch(`/api/credentials/${id}`);
    return response.json() as Promise<CredentialResponse>;
  }

  async createCredential(body: CreateCredentialRequest): Promise<CredentialResponse> {
    const response = await this._fetch('/api/credentials', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<CredentialResponse>;
  }

  async updateCredential(id: string, body: UpdateCredentialRequest): Promise<CredentialResponse> {
    const response = await this._fetch(`/api/credentials/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<CredentialResponse>;
  }

  async deleteCredential(id: string): Promise<void> {
    await this._fetch(`/api/credentials/${id}`, { method: 'DELETE' });
  }

  async listAccessLogs(query?: Record<string, string>): Promise<ListResponse<AccessLogEntryResponse>> {
    const params = query ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(`/api/credentials/access-log${params}`);
    return response.json() as Promise<ListResponse<AccessLogEntryResponse>>;
  }

  async revokeCredential(id: string, reason?: string): Promise<CredentialResponse> {
    const response = await this._fetch(`/api/credentials/${id}/revoke`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    return response.json() as Promise<CredentialResponse>;
  }

  // System Credentials
  async listSystemCredentials(query?: Record<string, string>): Promise<ListResponse<CredentialResponse>> {
    const params = query ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(`/api/credentials/system${params}`);
    return response.json() as Promise<ListResponse<CredentialResponse>>;
  }

  async createSystemCredential(body: CreateCredentialRequest): Promise<CredentialResponse> {
    const response = await this._fetch('/api/credentials/system', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<CredentialResponse>;
  }

  async updateSystemCredential(id: string, body: UpdateCredentialRequest): Promise<CredentialResponse> {
    const response = await this._fetch(`/api/credentials/system/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<CredentialResponse>;
  }

  async deleteSystemCredential(id: string): Promise<void> {
    await this._fetch(`/api/credentials/system/${id}`, { method: 'DELETE' });
  }

  async enableSystemCredential(id: string): Promise<SystemCredentialEnablementResponse> {
    const response = await this._fetch(`/api/credentials/system/${id}/enable`, { method: 'POST' });
    return response.json() as Promise<SystemCredentialEnablementResponse>;
  }

  async disableSystemCredential(id: string): Promise<SystemCredentialEnablementResponse> {
    const response = await this._fetch(`/api/credentials/system/${id}/disable`, { method: 'POST' });
    return response.json() as Promise<SystemCredentialEnablementResponse>;
  }

  async listEnabledSystemCredentials(): Promise<ListResponse<CredentialResponse>> {
    const response = await this._fetch('/api/credentials/system/enabled');
    return response.json() as Promise<ListResponse<CredentialResponse>>;
  }

  // Users
  async listUsers(query?: Record<string, string>): Promise<ListResponse<TenantUserResponse>> {
    const params = query ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(`/api/users${params}`);
    return response.json() as Promise<ListResponse<TenantUserResponse>>;
  }

  async createUser(body: { email: string; role: string; display_name?: string | undefined }): Promise<TenantUserResponse> {
    const response = await this._fetch('/api/users', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<TenantUserResponse>;
  }

  async updateUser(id: string, body: { role?: string | undefined; display_name?: string | undefined }): Promise<TenantUserResponse> {
    const response = await this._fetch(`/api/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<TenantUserResponse>;
  }

  async deactivateUser(id: string): Promise<TenantUserResponse> {
    const response = await this._fetch(`/api/users/${id}/deactivate`, {
      method: 'POST',
    });
    return response.json() as Promise<TenantUserResponse>;
  }

  // Slash commands
  async listSlashCommands(sessionId?: string): Promise<{ name: string; description: string }[]> {
    const params = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : '';
    const response = await this._fetch(`/api/slash_commands${params}`);
    const body = (await response.json()) as { data?: { name: string; description: string }[] };
    return body.data ?? [];
  }

  // Changelog endpoints
  async listChangelog(query?: Record<string, string>): Promise<ListResponse<ChangelogReleaseResponse>> {
    const params = query ? '?' + new URLSearchParams(query).toString() : '';
    const response = await this._fetch(`/api/changelog${params}`);
    return response.json() as Promise<ListResponse<ChangelogReleaseResponse>>;
  }

  // Plan actions
  async getPlan(planId: string): Promise<PlanResponse> {
    const response = await this._fetch(`/api/plans/${planId}`);
    return response.json() as Promise<PlanResponse>;
  }

  async approvePlan(planId: string): Promise<PlanResponse & { conversation_id?: string | null; trigger_ready?: boolean }> {
    const response = await this._fetch(`/api/plans/${planId}/approve`, { method: 'POST', body: '{}' });
    return response.json() as Promise<PlanResponse & { conversation_id?: string | null; trigger_ready?: boolean }>;
  }

  async denyPlan(planId: string, reason?: string): Promise<PlanResponse> {
    const response = await this._fetch(`/api/plans/${planId}/deny`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    return response.json() as Promise<PlanResponse>;
  }

  // Tool responses
  async submitToolResponse(body: { tool_call_id: string; payload?: Record<string, unknown> }): Promise<unknown> {
    const response = await this._fetch('/api/tool_responses', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json();
  }
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  return new ApiClient(config);
}
